<template>
  <div
    ref="group"
    class="max-w-full"
    :class="[marginClass, cssContainerStyles]"
    @mousedown="mouseDown"
    @touchstart="mouseDown"
    :style="containerStyle"
  >
    <slot />
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from 'vue'
import type { ButtonSize } from './button-size'
import { useMousePressed } from '@vueuse/core'

interface Props {
  direction?: 'horizontal' | 'vertical'
  size?: ButtonSize
}

const { size = 'S', direction = 'horizontal' } = defineProps<Props>()

const group = useTemplateRef<HTMLDivElement>('group')

const pressedButtonIndex = ref<number | undefined>(undefined)

const initialSize = ref<number | null>(null)

const cssContainerStyles = computed(() => {
  if (direction == 'vertical') {
    return 'flex flex-col'
  }
  return 'flex flex-row'
})

const mouseDown = (event: MouseEvent | TouchEvent) => {
  const button = (event.target as HTMLElement).closest('button')

  if (button) {
    pressedButtonIndex.value = getIndexOfButton(button)
  } else {
    pressedButtonIndex.value = undefined
  }
  return true
}

const { pressed } = useMousePressed()

watch(pressed, (isPressed) => {
  if (!isPressed) {
    pressedButtonIndex.value = undefined
    for (const button of allButtons()) {
      button.removeAttribute('style')
    }
  }
})

const getIndexOfButton = (button: HTMLButtonElement) => {
  if (group.value) {
    return Array.from(allButtons()).indexOf(button)
  }

  return undefined
}

const pressedButton = computed(() => {
  if (pressedButtonIndex.value != undefined && allButtons()) {
    return allButtons()[pressedButtonIndex.value]
  }

  return undefined
})

const neighbourButtons = computed(() => {
  if (pressedButtonIndex.value != undefined && allButtons()) {
    return Array.from(allButtons()).filter((_, index) => {
      return neighbourIndexes.value.includes(index)
    })
  }

  return []
})

const allButtons = () => {
  return group.value?.querySelectorAll('button') ?? []
}

const neighbourIndexes = computed(() => {
  if (pressedButtonIndex.value != undefined) {
    return [pressedButtonIndex.value - 1, pressedButtonIndex.value + 1]
  }
  return []
})

const growAmout = computed(() => {
  if (size == 'XS') {
    return 24
  }
  if (size == 'S') {
    return 16
  }
  if (size == 'M') {
    return 16
  }
  if (size == 'L') {
    return 24
  }
  return 24
})

const marginClass = computed(() => {
  if (direction == 'vertical') {
    const cssClass = '[&_button]:first:mt-0 [&_button]:last:mb-0 [&_button]:box-content'
    if (size == 'XS') {
      return cssClass + ' [&_button]:my-[9px]'
    }

    return cssClass + ' [&_button]:my-[4px]'
  }

  const cssClass = '[&_button]:first:ml-0 [&_button]:last:mr-0'
  if (size == 'XS') {
    return cssClass + ' [&_button]:mx-[9px]'
  }

  return cssClass + ' [&_button]:mx-[4px]'
})

const margin = computed(() => {
  if (size == 'XS') {
    return 9
  }

  return 4
})

const containerStyle = computed(() => {
  if (initialSize.value !== null) {
    if (direction == 'vertical') {
      return {
        height: `${initialSize.value}px`,
      }
    }
    return {
      width: `${initialSize.value}px`,
    }
  }
  return {}
})

watch(
  () => pressedButtonIndex.value,
  (newValue) => {
    if (newValue != undefined && group) {
      growButton(pressedButton.value)

      for (const button of neighbourButtons.value) {
        shrinkButton(button)
      }
    }
  },
)

const shrinkButton = (element: HTMLElement) => {
  const computedStyles = window.getComputedStyle(element)

  const marginFactor =
    pressedButtonIndex.value == 0 || pressedButtonIndex.value == allButtons().length - 1 ? 1 : 2
  const reductionAmount =
    (growAmout.value - margin.value * marginFactor) / neighbourButtons.value.length

  const currentPadding =
    direction == 'vertical' ? computedStyles['paddingTop'] : computedStyles['paddingLeft']
  const currentPaddingNum = parseFloat(currentPadding) || 0

  if (currentPaddingNum >= reductionAmount / 2) {
    const newPaddingNum = Math.max(0, currentPaddingNum - reductionAmount / 2)
    if (direction == 'horizontal') {
      element.style.setProperty('padding-left', `${newPaddingNum}px`, 'important')
      element.style.setProperty('padding-right', `${newPaddingNum}px`, 'important')
    } else {
      element.style.setProperty('padding-top', `${newPaddingNum}px`, 'important')
      element.style.setProperty('padding-bottom', `${newPaddingNum}px`, 'important')
    }
  } else {
    if (direction == 'horizontal') {
      const currentWidthNum = parseFloat(computedStyles['width']) || 0
      element.style.setProperty('width', currentWidthNum - reductionAmount + 'px', 'important')
    } else {
      const currentHeightNum = parseFloat(computedStyles['height']) || 0
      element.style.setProperty('height', currentHeightNum - reductionAmount + 'px', 'important')
    }
  }
}

const growButton = (element: HTMLElement | undefined) => {
  if (element && allButtons().length > 1) {
    const computedStyles = window.getComputedStyle(element)

    const currentPadding =
      direction == 'vertical' ? computedStyles['paddingTop'] : computedStyles['paddingLeft']
    const currentPaddingNum = parseFloat(currentPadding) || 0
    const newPaddingNum = currentPaddingNum + growAmout.value / 2

    if (direction == 'horizontal') {
      element.style.setProperty('margin-left', '0', 'important')
      element.style.setProperty('margin-right', '0', 'important')
      element.style.setProperty('padding-left', `${newPaddingNum}px`, 'important')
      element.style.setProperty('padding-right', `${newPaddingNum}px`, 'important')
    } else {
      element.style.setProperty('margin-top', '0', 'important')
      element.style.setProperty('margin-bottom', '0', 'important')
      element.style.setProperty('padding-top', `${newPaddingNum}px`, 'important')
      element.style.setProperty('padding-bottom', `${newPaddingNum}px`, 'important')
    }
  }
}
</script>
